import { useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Sparkles, X, Loader2, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useModelDialog, useAIModelBackend } from "../hooks";
import { ModelFilters } from "./ModelFilters";
import { ModelGrid } from "./ModelGrid";
import { cn } from "@/lib/utils";

export const EnhancedAIModelSelectorDialog = () => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    isOpen,
    closeModal,
    handleScroll: hookHandleScroll,
    filteredCount,
    totalModels,
    isExternalMode,
  } = useModelDialog();

  // 🎯 Backend verilerini sadece modal açıkken yükle
  const { isLoading, error } = useAIModelBackend(
    isExternalMode ? false : isOpen
  );

  // Scroll handler
  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      hookHandleScroll(scrollRef.current);
    }
  }, [hookHandleScroll]);

  return (
    <Tooltip.Provider
      delayDuration={200}
      skipDelayDuration={100}
      disableHoverableContent={false}
    >
      <Dialog open={isOpen} onOpenChange={closeModal}>
        <DialogContent
          open={isOpen}
          className={cn(
            "ai-model-dialog overflow-hidden p-0",
            "max-w-6xl w-full sm:w-[95%] rounded-3xl",
            "bg-white dark:bg-gray-900",
            // "border border-gray-200/80 dark:border-gray-700/80",
            "shadow-2xl shadow-black/10 dark:shadow-black/40"
            // "ring-1 ring-black/5 dark:ring-white/10"
          )}
          style={{
            // Mobile: use dynamic viewport height for safe sizing
            maxHeight: "92dvh",
          }}
          data-state={isOpen ? "open" : "closed"}
        >
          {/* Decorative background */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-transparent to-purple-500/10 dark:from-indigo-500/15 dark:to-purple-500/15" />

          <div
            className="relative z-10 flex flex-col h-full"
            style={{ maxHeight: "inherit" }}
          >
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="flex-shrink-0 px-4 py-4 sm:px-7 sm:py-6 border-b border-gray-200/70 dark:border-gray-700/70"
            >
              <DialogHeader className="space-y-2">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-600/10 dark:bg-indigo-500/15">
                      <Sparkles className="h-5 w-5 text-indigo-600 dark:text-indigo-300" />
                    </div>
                    <div className="min-w-0">
                      <DialogTitle className="text-xl font-semibold tracking-tight text-text-primary dark:text-gray-100">
                        {t("aiModelSelection.title")}
                      </DialogTitle>
                      <DialogDescription className="mt-1 text-sm text-text-secondary dark:text-gray-400">
                        {t("aiModelSelection.description")}
                      </DialogDescription>
                    </div>
                  </div>

                  <DialogClose asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-xl text-text-secondary hover:text-text-primary hover:bg-bg-secondary dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800"
                    >
                      <X className="h-4 w-4" />
                      <span className="sr-only">
                        {t("aiModelSelection.close")}
                      </span>
                    </Button>
                  </DialogClose>
                </div>
              </DialogHeader>

              {/* Arama: External mode'da local search, backend mode'da server search */}
              <div className="mt-4">
                <ModelFilters />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2, delay: 0.1 }}
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto px-4 py-4 sm:px-7 sm:py-6 min-h-0"
              style={{
                scrollbarWidth: "thin",
                scrollBehavior: "smooth",
                WebkitOverflowScrolling: "touch",
              }}
            >
              {error && !isExternalMode ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-full max-w-md rounded-2xl border border-red-200/60 dark:border-red-900/40 bg-red-50/40 dark:bg-red-950/10 p-5">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl bg-red-500/10 dark:bg-red-500/15">
                        <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-300" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-red-700 dark:text-red-200">
                          {t("aiModelSelection.error")}
                        </p>
                        <p className="mt-1 text-xs sm:text-sm text-text-secondary dark:text-gray-400 break-words">
                          {String(error)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isLoading && !isExternalMode ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-full max-w-md flex flex-col items-center justify-center rounded-2xl border border-border-secondary dark:border-gray-700 bg-bg-secondary dark:bg-gray-800 py-10 gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/70 dark:bg-gray-900/40 ring-1 ring-black/5 dark:ring-white/10">
                      <Loader2 className="h-6 w-6 animate-spin text-indigo-600 dark:text-indigo-300" />
                    </div>
                    <p className="text-sm font-medium text-text-secondary dark:text-gray-400">
                      {t("aiModelSelection.loading")}
                    </p>
                  </div>
                </div>
              ) : (
                <ModelGrid />
              )}
            </motion.div>

            {/* Footer */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: 0.2 }}
              className="flex-shrink-0 px-4 py-3 sm:px-7 sm:py-4 border-t border-gray-200/70 dark:border-gray-700/70 bg-white/60 dark:bg-gray-900/30"
            >
              <div className="flex items-center justify-between text-xs sm:text-sm text-text-secondary dark:text-gray-400">
                <span>
                  {t("aiModelSelection.modelsShowing", {
                    count: filteredCount,
                  })}
                </span>
                {!isExternalMode && (
                  <span>
                    {t("aiModelSelection.totalModels", { total: totalModels })}
                  </span>
                )}
              </div>
            </motion.div>
          </div>
        </DialogContent>
      </Dialog>
    </Tooltip.Provider>
  );
};
